import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import clientExtension from 'virtual:databink/client';
import type { WebExtension } from './extension-types.js';
import { watchColorMode } from './lib/color-mode.js';
import { ExtensionProvider } from './lib/slots.js';
import { onUnauthenticated, queryClient } from './lib/trpc.js';
import { router } from './router.js';
import '@fontsource-variable/jetbrains-mono';
import '@fontsource-variable/plus-jakarta-sans';
import './styles.css';

/** The client's screen extensions, when this build was made for a client (DATABINK_CLIENT). */
function extensionOf(value: unknown): WebExtension | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<WebExtension>;
  if (
    typeof candidate.client !== 'string' ||
    !candidate.slots ||
    typeof candidate.slots !== 'object'
  ) {
    console.error(
      'The client web extension does not export defineWebExtension({ client, slots }); its screens are left out.',
    );
    return null;
  }
  // Only image addresses: anything else would draw a broken image.
  const brand = Object.fromEntries(
    Object.entries(candidate.brand ?? {}).filter(([, value]) => typeof value === 'string'),
  );
  return { ...(candidate as WebExtension), brand };
}

const extension = extensionOf(clientExtension);

watchColorMode();

// The client's icon in the browser tab; without one, index.html leaves the tab's icon blank.
const favicon = extension?.brand?.favicon ?? extension?.brand?.icon;
if (favicon) document.querySelector('link[rel="icon"]')?.setAttribute('href', favicon);

onUnauthenticated(() => {
  const here = router.state.location;
  if (here.pathname === '/login' || here.pathname === '/accept-invite') return;
  setTimeout(() => {
    queryClient.clear();
    void router.navigate({ to: '/login', search: here.href === '/' ? {} : { next: here.href } });
  });
});

const root = document.getElementById('root');
if (!root) throw new Error('index.html has no #root element');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ExtensionProvider extension={extension}>
        <RouterProvider router={router} />
      </ExtensionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
